<template>
  <v-card class="mt-2">
    <v-card-title class="justify-space-between">
      Cohort by {{ title }}
      <v-icon
        @click="onDownloadClick"
      >mdi-download</v-icon>
    </v-card-title>
    <v-divider />
    <v-data-table
      :headers="headers"
      :items="report"
      :loading="isLoading"
      hide-default-footer
      disable-pagination
    >
      <template
        v-for="(header, index) in headers"
        #[`header.${header.value}`]
      >
        <span style="white-space: nowrap">
          {{ header.text }}

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-icon
                class="ml-1"
                color="primary"
                dark
                v-bind="attrs"
                v-on="on"
              >
                help
              </v-icon>
            </template>
            <span>{{ getTooltipText(header.value, index - 1) }}</span>
          </v-tooltip>
        </span>
      </template>
      <template #item="{item}">
        <tr>
          <td
            v-for="header in headers"
            :style="{background: defaultHeaderKeys.includes(header.value) ? '' : getCellColor(getCellPercentage(item.members, get(item, header.value)))}"
          >
            <template v-if="defaultHeaderKeys.includes(header.value)">
              {{ get(item, header.value) }}
            </template>
            <template v-else>
              {{ getCellText(getCellPercentage(item.members, get(item, header.value))) }}
            </template>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { get, map } from 'lodash'
import moment from 'moment'

const defaultHeaders = [
  { text: 'Cohort', value: 'cohortBy', sortable: false },
  { text: 'FTD', value: 'members', sortable: false },
]

export default {
  props: {
    report: {
      type: Array,
      default: () => ([])
    },
    reportSettings: {
      type: Object,
      default: () => ({})
    },
    isLoading: Boolean
  },
  data() {
    return {
      defaultHeaderKeys: map(defaultHeaders, 'value')
    }
  },
  computed: {
    headers() {
      if (!this.report.length) {
        return defaultHeaders
      }

      const maxEntires = this.report.reduce((acc, currentValue) => currentValue.entries.length > acc ? currentValue.entries.length : acc, 0)
      const valueHeaders = []
      for (let i = 0; i < maxEntires; i++) {
        valueHeaders.push({
          text: this.getHeaderText(this.report[0].type, i + 1),
          value: `entries.${i}`,
          sortable: false
        })
      }
      return [
        ...defaultHeaders,
        ...valueHeaders
      ]
    },
    title() {
      if (!this.report.length) {
        return
      }

      return this.getCohortByText(this.report[0].type)
    }
  },
  methods: {
    get,
    getCellPercentage(totalMembers, cellValue) {
      if (!cellValue) {
        return 0
      }

      return (100 * cellValue) / totalMembers
    },
    getCellText(cellPercentage) {
      return cellPercentage ? `${cellPercentage.toFixed(1)}%` : '-'
    },
    getCellColor(value) {
      if (value < 20) {
        return '#fff'
      }
      if (value < 40) {
        return '#9dc6fc'
      }
      if (value < 60) {
        return '#65a3f5'
      }
      if (value < 80) {
        return '#2f80ed'
      }
      if (value < 100) {
        return '#0c6ae9'
      }
      if (value === 100) {
        return '#005ad4'
      }
    },
    getHeaderText(type, n) {
      switch (type) {
        case 'days':
          return `Day ${n}`
        case 'weeks':
          return `Week ${n}`
        case 'deposit':
          return `Deposit ${n}`
      }
    },

    getCohortByText(type) {
      switch (type) {
        case 'days':
          return 'day'
        case 'weeks':
          return 'week'
        case 'deposit':
          return 'deposit'
      }
    },

    getReportAsCSV() {
      const headers = map(this.headers, 'text').join(',')
      const rows = this.report.map(({cohortBy, members, entries}) => {
        return [cohortBy, members, ...this.headers.slice(defaultHeaders.length).map((_, index) => this.getCellText(this.getCellPercentage(members, entries[index])))].join(',')
      }).join('\n')

      return `${headers}\n${rows}`
    },

    onDownloadClick() {
      const blob = new Blob([this.getReportAsCSV()], {type: 'application/octet-stream'});
      const blobURL = window.URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = blobURL;
      tempLink.setAttribute('download', `cohorts_export_${moment().format()}.csv`);

      document.body.appendChild(tempLink);
      tempLink.click();

      tempLink.remove();
      window.URL.revokeObjectURL(blobURL);
    },

    getTooltipText(key, value) {
      if (key === 'cohortBy') {
        return `Group of users by ${this.reportSettings[key]}`
      }
      if (key === 'members') {
        return `Number of first-time deposits for the selected cohort`
      }

      if (this.reportSettings.groupBy === 'deposit') {
        return `Percentage of users who have made at least ${value} deposits`
      } else {
        return `Percentage of users who have made a bet on ${this.reportSettings.groupBy === 'days' ? 'day' : 'week'} ${value}`
      }
    }
  }
}
</script>
